// Lib
import React, { useEffect } from 'react';
import ReactPlayer from 'react-player/lazy'; // Kept for YouTube/Twitch embed support

// Components
import TikTok from '@components/TiktokPlayer';
import SpotifyPlayer from '@components/SpotifyPlayer';
import { Button } from '@components/ui/button';

// Utils
import getLinkWithUTMParams from '@utils/getLinkWithUTMParams';

// Types
interface MediaAttachment {
  type?: string;
  url?: string;
}

interface PostAttachment {
  media?: MediaAttachment;
}

interface PostSource {
  toLowerCase: () => string;
}

interface Post {
  attachments?: PostAttachment[];
  source?: PostSource | string;
}

interface Slide {
  post?: Post;
  imageUrl?: string;
  title?: string;
  url?: string;
}

interface SlideModalProps {
  slide?: Slide;
  _advancedColor?: string;
  colorMode?: string;
  avatarSrc?: string;
  fullName?: string;
  pathname?: string;
  username?: string;
  marbleId?: string;
}

const getInitials = (name?: string): string =>
  (name || '?')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const SlideModal: React.FC<SlideModalProps> = ({
  slide,
  _advancedColor,
  colorMode,
  avatarSrc,
  fullName,
  pathname,
  username,
  marbleId,
}) => {
  const videoSrc =
    slide?.post?.attachments?.[0]?.media?.type === 'video'
      ? slide?.post?.attachments?.[0]?.media?.url
      : '';
  const source =
    typeof slide?.post?.source === 'string'
      ? slide?.post?.source?.toLowerCase()
      : (slide?.post?.source as PostSource)?.toLowerCase?.();

  useEffect(() => {
    const popupClose = document.getElementById('popup-close');
    popupClose && popupClose.classList.remove('classic');
    popupClose && popupClose.classList.add(colorMode);
    return () => {
      popupClose && popupClose.classList.remove(colorMode);
    };
  }, [colorMode]);

  return (
    <div className="body-container flex flex-col py-5">
      <div className="flex items-center pl-5 pr-[50px]">
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt=""
            className="mr-2.5 size-[60px] min-h-[60px] min-w-[60px] shrink-0 rounded-full object-cover"
          />
        ) : (
          <span className="mr-2.5 flex size-[60px] min-h-[60px] min-w-[60px] shrink-0 items-center justify-center rounded-full bg-marble-bg-100 text-base font-bold text-marble-bg-600">
            {getInitials(fullName)}
          </span>
        )}
        <h2 className="line-clamp-1 w-full break-all text-lg font-bold text-foreground">
          {fullName}
        </h2>
      </div>
      {['youtube', 'twitch', 'tiktok', 'instagram'].includes(source) &&
      videoSrc ? (
        source === 'tiktok' ? (
          <div className="mt-5 max-[743px]:max-h-[60vh] max-[743px]:overflow-auto [&_img]:size-full">
            <TikTok post={slide?.post?.attachments[0]} />
          </div>
        ) : (
          <div className="relative mt-5 [&_iframe]:mx-auto [&_iframe]:block [&_iframe]:!h-[315px] [&_iframe]:!w-full">
            <ReactPlayer
              url={videoSrc}
              playing={false}
              width="100%"
              height={315}
              volume={1}
              loop={false}
              controls={true}
              config={{
                youtube: {
                  embedOptions: {
                    playsinline: 1,
                  },
                },
              }}
            />
          </div>
        )
      ) : slide?.imageUrl ? (
        <>
          <img
            src={slide?.imageUrl}
            alt=""
            className="mx-auto mt-5 h-full w-full"
          />
          {source === 'spotify' ? (
            <SpotifyPlayer
              media={{ ...slide?.post?.attachments[0], type: 'audio' }}
            />
          ) : null}
        </>
      ) : null}
      <div className="px-5 pt-5">
        {slide?.title ? (
          <p className="mb-5 line-clamp-2 w-full break-all text-sm text-foreground">
            {slide?.title}
          </p>
        ) : null}
        {slide?.url ? (
          <Button
            title={getLinkWithUTMParams(slide?.url, username, marbleId)}
            onClick={() => {
              window.dataLayer = window.dataLayer || [];

              window.dataLayer.push({
                event: 'link_click',
                url: slide?.url,
                pagePath: pathname,

                // custom param
                custom_url: slide?.url,
              });

              window.open(
                getLinkWithUTMParams(slide?.url, username, marbleId),
                '_blank',
              );
            }}
          >
            Visit website
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default SlideModal;
